import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'approved' || $status === 'activated' ? '#2e7d3215' :
    $status === 'waiting' || $status === 'submitted' ? '#ed6c0215' :
    $status === 'rejected' ? '#d32f2f15' :
    $status === 'canceled' || $status === 'terminated' ? '#9e9e9e15' :
    '#0291EF15'};
  color: ${({ $status }) =>
    $status === 'approved' || $status === 'activated' ? '#2e7d32' :
    $status === 'waiting' || $status === 'submitted' ? '#ed6c02' :
    $status === 'rejected' ? '#d32f2f' :
    $status === 'canceled' || $status === 'terminated' ? '#9e9e9e' :
    '#0291EF'};
`;

// Mirrors the backend AllowedTypes. Dependent-typed applications require a dependent pick.
export const APPLICATION_TYPES = ['new-benefit', 'add-dependent', 'remove-dependent', 'change-coverage'];
const DEPENDENT_TYPES = ['add-dependent', 'remove-dependent'];

const useMyBenefitApplicationsApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 });
    lastValidQueryParams.current = params;
    setFetching(true);
    api.get(`/v1/my-benefit-applications?${new URLSearchParams(params)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (formData) => {
    try {
      const isDependentType = DEPENDENT_TYPES.includes(formData.applicationType);
      const payload = {
        benefitId: formData.benefitId,
        applicationType: formData.applicationType,
        relatedDependentId: isDependentType ? (formData.relatedDependentId || null) : null,
        requestedActivationDate: formData.requestedActivationDate,
        notes: formData.notes || null,
        // GenericFormDrawer's pictureUpload presigns + uploads, leaving a URL string.
        documents: formData.documentUrl
          ? [{ name: t('benefitApplication.documentName'), mediaUrl: formData.documentUrl }]
          : null,
      };
      await api.post('/v1/my-benefit-applications', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh, t]);

  const cancel = useCallback(async (id) => {
    try { await api.put(`/v1/my-benefit-applications/${id}/cancel`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const typeLabels = useMemo(() => ({
    'new-benefit': t('benefitApplication.typeNewBenefit'),
    'add-dependent': t('benefitApplication.typeAddDependent'),
    'remove-dependent': t('benefitApplication.typeRemoveDependent'),
    'change-coverage': t('benefitApplication.typeChangeCoverage'),
  }), [t]);

  const statusLabels = useMemo(() => ({
    draft: t('benefitApplication.statusDraft'),
    submitted: t('benefitApplication.statusSubmitted'),
    waiting: t('benefitApplication.statusWaiting'),
    approved: t('benefitApplication.statusApproved'),
    rejected: t('benefitApplication.statusRejected'),
    canceled: t('benefitApplication.statusCanceled'),
    activated: t('benefitApplication.statusActivated'),
    terminated: t('benefitApplication.statusTerminated'),
  }), [t]);

  const typeOptions = useMemo(() => APPLICATION_TYPES.map((id) => ({ id, name: typeLabels[id] })), [typeLabels]);

  // Self-service benefit catalog (no BN permission needed) — feeds the picker.
  const benefitAsync = useMemo(() => {
    const toOption = (b) => ({ id: b.id, name: b.categoryName ? `${b.name} — ${b.categoryName}` : b.name });
    const fetchAll = async () => {
      const res = await api.get('/v1/my-benefit-applications/selectable-benefits');
      return res.data?.data || [];
    };
    return {
      onSearch: async (q) => {
        const items = await fetchAll();
        const filtered = q ? items.filter((b) => (b.name || '').toLowerCase().includes(q.toLowerCase())) : items;
        return filtered.map(toOption);
      },
      onGetById: async (id) => {
        if (!id) return null;
        const b = (await fetchAll()).find((x) => x.id === id);
        return b ? toOption(b) : { id, name: '' };
      },
    };
  }, []);

  // The employee's own dependents (for add/remove-dependent applications).
  const dependentAsync = useMemo(() => {
    const toOption = (d) => ({ id: d.id, name: `${d.firstName || ''} ${d.lastName || ''}`.trim() });
    const fetchAll = async () => {
      const res = await api.get('/v1/my-benefit-dependents');
      const r = res.data?.data;
      return Array.isArray(r) ? r : (r?.data || []);
    };
    return {
      onSearch: async (q) => {
        const mapped = (await fetchAll()).map(toOption);
        return q ? mapped.filter((d) => d.name.toLowerCase().includes(q.toLowerCase())) : mapped;
      },
      onGetById: async (id) => {
        if (!id) return null;
        const d = (await fetchAll()).find((x) => x.id === id);
        return d ? toOption(d) : { id, name: '' };
      },
    };
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'benefitName', label: t('benefitApplication.benefit'), options: { filter: false, sort: false } },
    {
      name: 'applicationType',
      label: t('benefitApplication.type'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: { defaultOp: 'eq', names: typeOptions.map((o) => o.name), renderValue: (v) => v },
        customBodyRender: (v) => typeLabels[v] || v,
      },
    },
    {
      name: 'status',
      label: t('benefitApplication.status'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => <StatusBadge $status={v}>{statusLabels[v] || v}</StatusBadge>,
      },
    },
    dateFilterColumn('requestedActivationDate', t('benefitApplication.requestedActivationDate'), i18n.language),
    dateFilterColumn('createDate', t('benefitApplication.submittedDate'), i18n.language),
  ], [t, i18n.language, typeLabels, statusLabels, typeOptions]);

  const objectSchema = useMemo(() => [
    {
      name: 'applicationType', label: t('benefitApplication.type'), type: 'select', grid: 12, required: true,
      options: typeOptions, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'benefitId', label: t('benefitApplication.benefit'), type: 'asyncSelect', grid: 12, required: true,
      ...benefitAsync,
    },
    {
      // Required only when visible — GenericFormDrawer skips required checks on hidden fields.
      name: 'relatedDependentId', label: t('benefitApplication.relatedDependent'), type: 'asyncSelect', grid: 12,
      required: true,
      hidden: (fd) => !DEPENDENT_TYPES.includes(fd.applicationType),
      ...dependentAsync,
    },
    { name: 'requestedActivationDate', label: t('benefitApplication.requestedActivationDate'), type: 'date', grid: 12, required: true },
    { name: 'notes', label: t('benefitApplication.notes'), type: 'textarea', grid: 12 },
    {
      name: 'documentUrl', label: t('benefitApplication.documents'), type: 'pictureUpload', grid: 12,
      shape: 'square', accept: 'image/*,application/pdf',
      validTypes: ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'],
    },
  ], [t, typeOptions, benefitAsync, dependentAsync]);

  return { objectSchema, columns, data, fetching, pagination, create, cancel, refresh, statusLabels };
};

export default useMyBenefitApplicationsApi;
